import { convertSitemapsToSitemapNodes, getSitemapMapOrderValue, SitemapNode, SiteMapNodeTypes, SitemapRootNode, SitemapVersionLevelCreateRequest } from "@iventis/sitemaps";
import { ISitemapApiContext } from "@iventis/sitemaps/src/context/sitemap-api-context";
import { SitemapVersionLevelCreateResponse } from "@iventis/domain-model/model/sitemapVersionLevelCreateResponse";
import { SitemapVersionLevelCadConfig } from "@iventis/domain-model/model/sitemapVersionLevelCadConfig";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { NodeServices } from "@iventis/tree-browser";
import { convertStringDatesToDates, readFileAsJson } from "@iventis/utilities";
import { SitemapVersion } from "@iventis/domain-model/model/sitemapVersion";
import { SitemapVersionLevel } from "@iventis/domain-model/model/sitemapVersionLevel";
import { SitemapVersionLevelUploadMethod } from "@iventis/domain-model/model/sitemapVersionLevelUploadMethod";
import { SitemapVersionLevelSignedUrls } from "@iventis/domain-model/model/sitemapVersionLevelSignedUrls";
import { CadStyle } from "@iventis/cad-georeferencer/src/types/style";
import { Asset } from "@iventis/domain-model/model/asset";
import { parseISO } from "date-fns";
import { api } from "../api/api";
import { createSitemapVersionLevelPatchRequestBody } from "./sitemap-api-helpers";

/** The root id has to be made up of both the project id and the instance name because we need both in our requests to the admin dashboard */
export const getProjectIdAndInstanceNameFromRootId = (id: string) => id.split("|");
export const createRootIdFromProjectIdAndInstanceName = (projectId: string, instanceName) => `${projectId}|${instanceName}`;

export const sitemapServices: NodeServices<SitemapRootNode> = {
    getTree: async (id) => {
        const [projectId, instanceName] = getProjectIdAndInstanceNameFromRootId(id);

        const data = await getSitemaps(instanceName, projectId);
        const orderedSitemaps = data.sort((a, b) => getSitemapMapOrderValue(b.mapOrder) - getSitemapMapOrderValue(a.mapOrder));

        // Convert the string dates we recieve from the backend into Date's
        convertStringDatesToDates(
            orderedSitemaps.flatMap((sitemap) => sitemap.versions),
            "date"
        );

        // Convert sitemaps into a tree browser node implementation of sitemaps
        const nodeData = convertSitemapsToSitemapNodes(orderedSitemaps, id);

        // Wrap all the sitemaps around a blank root node
        return {
            id,
            sourceId: id,
            name: undefined,
            type: SiteMapNodeTypes.Root,
            childNodes: nodeData,
            childCount: nodeData.length,
        } as SitemapRootNode;
    },
    addNode: async () => {
        throw new Error(`Sitemap tree browser only adds nodes locally. Please update on the server first, then call "addNodesLocally()"`);
    },
    updateNode: async () => {
        throw new Error(`Sitemap tree browser only updates nodes locally. Please update on the server first, then call "updateNodeLocally()"`);
    },
    deleteNodes: async (nodes, tree) => {
        const [projectId, instanceName] = getProjectIdAndInstanceNameFromRootId(tree.id);
        const { Sitemap: sitemaps } = nodes.reduce(
            (acc, node) => (node.type === SiteMapNodeTypes.Sitemap ? { [node.type]: [...(acc[node.type] ?? []), node] } : acc),
            {} as { [SiteMapNodeTypes.Sitemap]: SitemapNode[] }
        );
        const requests: Promise<unknown>[] = [];
        const ids: string[] = [];
        if (sitemaps?.length > 0) {
            const sitemapIds = sitemaps.map(({ id }) => id);
            const sitemapRequests = sitemapIds.map((id) => api.delete(`/instances/${instanceName}/projects/${projectId}/sitemaps/${id}`));
            requests.push(...sitemapRequests);
            ids.push(...sitemapIds);
        }
        await Promise.all(requests);
        return ids;
    },
    getNode: async () => null,
    moveNodes: async () => null,
    copyNodes: async () => null,
    getParents: async () => null,
};

export const createSitemapContext = (instanceName: string, projectId: string): ISitemapApiContext => ({
    async getSitemap(sitemapId: string): Promise<Sitemap> {
        return getSitemap(instanceName, projectId, sitemapId);
    },
    async createSitemap(sitemap): Promise<Sitemap> {
        const res = await api.post<Sitemap>(`/instances/${instanceName}/projects/${projectId}/sitemaps`, { ...sitemap, projectId });
        return res.data;
    },
    async updateSitemap(sitemap): Promise<Sitemap> {
        const res = await api.patch<Sitemap>(`/instances/${instanceName}/projects/${projectId}/sitemaps/${sitemap.id}`, { ...sitemap, projectId });
        return res.data;
    },
    async getSitemapVersion(sitemapId: string, sitemapVersionId: string): Promise<SitemapVersion> {
        const sitemap = await getSitemap(instanceName, projectId, sitemapId);
        const sitemapVersion = sitemap.versions.find((version) => version.id === sitemapVersionId);
        if (!sitemapVersion) {
            throw new Error(`Sitemap version with id ${sitemapVersionId} not found`);
        }
        return sitemapVersion;
    },
    async createSitemapVersion(sitemapVersion): Promise<SitemapVersion> {
        const { data } = await api.post<SitemapVersion>(`/instances/${instanceName}/projects/${projectId}/sitemaps/${sitemapVersion.sitemapId}/versions`, {
            ...sitemapVersion,
            date: new Date(Date.now()),
        });
        // Convert the string dates we recieve from the backend into Date's
        convertStringDatesToDates([data], "date");
        return data;
    },
    async updateSitemapVersion(sitemapVersion): Promise<SitemapVersion> {
        const { data } = await api.patch<SitemapVersion>(
            `/instances/${instanceName}/projects/${projectId}/sitemaps/${sitemapVersion.sitemapId}/versions/${sitemapVersion.id}`,
            sitemapVersion
        );
        // Convert the string dates we recieve from the backend into Date's
        convertStringDatesToDates([data], "date");
        return data;
    },
    async deleteSitemapVersion(id, sitemapId): Promise<void> {
        await api.delete<SitemapVersion>(`/instances/${instanceName}/projects/${projectId}/sitemaps/${sitemapId}/versions/${id}`);
    },
    async getSitemapVersionLevel(sitemapId: string, sitemapVersionId: string, sitemapVersionLevelId: string): Promise<SitemapVersionLevel> {
        const sitemap = await getSitemap(instanceName, projectId, sitemapId);
        const sitemapVersion = sitemap.versions.find((version) => version.id === sitemapVersionId);
        return sitemapVersion.sitemapVersionLevels.find((sitemapVersionLevel) => sitemapVersionLevel.id === sitemapVersionLevelId);
    },
    async getSitemapVersionLevelCadConfig(sitemapId: string, sitemapVersionIds: string, sitemapVersionLevelId: string): Promise<SitemapVersionLevelCadConfig> {
        const cadConfigResponse = await api.get(
            `instances/${instanceName}/projects/${projectId}/sitemaps/${sitemapId}/versions/${sitemapVersionIds}/levels/${sitemapVersionLevelId}/cad-config`
        );
        return cadConfigResponse.data;
    },
    async createSitemapVersionLevel(level, sitemapId): Promise<SitemapVersionLevelCreateResponse> {
        const createSitemapVersionLevelReqBody: Omit<SitemapVersionLevelCreateRequest, "perimeter"> & { perimeter: string } = { ...level };
        if (createSitemapVersionLevelReqBody.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles && level.uploadMethod === SitemapVersionLevelUploadMethod.Mbtiles) {
            // Read the uploaded file as json and append to the request
            const style = await readFileAsJson(level.style);
            createSitemapVersionLevelReqBody.style = style;
            createSitemapVersionLevelReqBody.perimeter = JSON.parse(level.perimeter);
        }
        const res = await api.post<SitemapVersionLevelCreateResponse>(
            `/instances/${instanceName}/projects/${projectId}/sitemaps/${sitemapId}/versions/${level.sitemapVersionId}/levels`,
            createSitemapVersionLevelReqBody
        );
        return res?.data;
    },
    async updateSitemapVersionLevel(level, sitemapId): Promise<string> {
        const requestBody = await createSitemapVersionLevelPatchRequestBody(level);

        const res = await api.patch<string>(
            `/instances/${instanceName}/projects/${projectId}/sitemaps/${sitemapId}/versions/${level.sitemapVersionId}/levels/${level.id}`,
            requestBody,
            { suppress404: true } // Sometimes when patching the level wont exist from creation yet. Retries should be handled by the caller.
        );
        return res?.data;
    },
    async deleteSitemapVersionLevel(id, sitemapVersionId, sitemapId): Promise<void> {
        await api.delete(`/instances/${instanceName}/projects/${projectId}/sitemaps/${sitemapId}/versions/${sitemapVersionId}/levels/${id}`);
    },
    async reassignSitemapLevels(reassignments) {
        await api.post(
            `/instances/${instanceName}/projects/${projectId}/sitemaps/${reassignments[0].sitemapId}/level-reassignments`,
            reassignments.map((reassignment) => ({ ...reassignment, projectId }))
        );
    },
    async getSitemapVersionLevelCadFiles(sitemapId, sitemapVersionId, sitemapVersionLevelId) {
        const cadFilesResponse = await api.get<SitemapVersionLevelSignedUrls>(
            `instances/${instanceName}/projects/${projectId}/sitemaps/${sitemapId}/versions/${sitemapVersionId}/levels/${sitemapVersionLevelId}/review-signed-urls`
        );
        const { geoJsonUrl, styleUrl } = cadFilesResponse.data;

        if (geoJsonUrl == null || styleUrl == null) {
            throw new Error("Geojson or style url has a null url returned");
        }
        const mapStyleResponse = (await fetch(styleUrl).then((res) => res.json())) as CadStyle;

        return { style: mapStyleResponse };
    },
    async getMapBackgrounds(): Promise<Asset[]> {
        const filter = [{ fieldName: "assetType", operator: "Eq", value: "MapBackground" }];
        const { data } = await api.post<Asset[]>(`/assets/filter`, filter);
        if (!data) {
            return [];
        }
        // Set the updatedAt date to now. This is a workaround to ensure the latest asset data is requested from S3.
        // This is necessary because google map background API key and the updatedAt is updated on the instance, but not in the admin dashboard.
        // By setting the updatedAt date to now, we ensure that the latest asset data is requested from S3 with the latest API key.
        // A better solution might be to have the admin dashboard handle API key rotation.
        const updatedData = data.map((asset) => ({
            ...asset,
            updatedAt: new Date(),
        }));
        return updatedData;
    },
    async getSitemapVersionLevelAsset(levelId: string): Promise<Asset> {
        const { data } = await api.get<Asset>(
            `/instances/${instanceName}/projects/00000000-0000-0000-0000-000000000000/sitemaps/00000000-0000-0000-0000-000000000000/versions/00000000-0000-0000-0000-000000000000/levels/${levelId}/asset`
        );
        data.updatedAt = parseISO(data.updatedAt.toString());
        return data;
    },
    async copySitemapVersion(sourceProjectId: string, sourceSitemapVersionId: string, destinationProjectId: string, destinationSitemapVersionId: string): Promise<void> {
        await api.post(`/instances/${instanceName}/projects/${projectId}/sitemaps/versions/levels/copy`, {
            sourceProjectId,
            sourceSitemapVersionId,
            destinationProjectId,
            destinationSitemapVersionId,
        });
    },
});

export async function getSitemaps(instanceName: string, projectId: string): Promise<Sitemap[]> {
    const { data } = await api.get<Sitemap[]>(`/instances/${instanceName}/projects/${projectId}/sitemaps`);
    return data;
}

async function getSitemap(instanceName: string, projectId: string, sitemapId: string): Promise<Sitemap> {
    const sitemap = await api.get<Sitemap>(`/instances/${instanceName}/projects/${projectId}/sitemaps/${sitemapId}`);
    return sitemap.data;
}

export async function copySitemapVersion(
    instanceName: string,
    sourceProjectId: string,
    sourceSitemapId: string,
    sourceSitemapVersionId: string,
    destinationProjectId: string,
    destinationSitemapId: string
): Promise<void> {
    const payload = { destinationSitemapId, destinationProjectId };
    const { data } = await api.post(`/instances/${instanceName}/projects/${sourceProjectId}/sitemaps/${sourceSitemapId}/versions/${sourceSitemapVersionId}/copy`, payload);
    return data;
}
